import type { ReactNode } from 'react';
import type { IServiceSupervisor, SelectedServiceSupervisors } from './ServiceGroupSettingsAddEdit';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    Button,
    DeleteButton,
    DropdownSearch,
    FormGroup,
    FormLabel,
    FullName,
    InputGroup,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';

interface IServiceSupervisorListAddEditProps {
    onChange: (selectedServiceSupervisors: SelectedServiceSupervisors) => Promise<void>;
    selectedServiceSupervisors: SelectedServiceSupervisors;
    serviceGroupSettingId?: string;
    serviceId: string;
}

interface IServiceSupervisorListAddEditState {
    selectedServiceSupervision: IServiceSupervisor | null;
}

export class ServiceSupervisorListAddEdit extends Component<
    IServiceSupervisorListAddEditProps,
    IServiceSupervisorListAddEditState
> {
    constructor(props: IServiceSupervisorListAddEditProps, context?: unknown) {
        super(props, context);

        this.state = {
            selectedServiceSupervision: null,
        };
    }

    public render() {
        const rows: ReactNode[] = [];

        for (const [key, selectedServiceSupervisor] of this.props.selectedServiceSupervisors.entrySeq().toArray() as [
            string,
            IServiceSupervisor,
        ][]) {
            rows.push(
                <TableRow key={key}>
                    <TableData>
                        <FullName
                            nameFamily={selectedServiceSupervisor.employment.practitioner.nameFamily}
                            nameGiven={selectedServiceSupervisor.employment.practitioner.nameGiven}
                            nameInfix={selectedServiceSupervisor.employment.practitioner.nameInfix}
                        />
                    </TableData>
                    <TableData>{selectedServiceSupervisor.employment.organization.name}</TableData>
                    <TableData align="right">
                        <DeleteButton
                            onClick={async () => await this.removeServiceSupervisor(selectedServiceSupervisor)}
                        />
                    </TableData>
                </TableRow>
            );
        }

        return (
            <>
                <h1 className="mb-4 mt-4">
                    {translateKey('common.selected') + ' ' + translateKey('serviceConsumersGroupSettings.supervisors')}
                </h1>

                {rows.length === 0 ? (
                    <> {translateKey('serviceConsumersGroupSettings.noSupervisor')} </>
                ) : (
                    <AdminTable>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translateKey('practitioner.practitioner')} </TableHeader>
                                <TableHeader>{translateKey('organization.organization')} </TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                    </AdminTable>
                )}

                <FormGroup>
                    <FormLabel>{translateKey('serviceConsumersGroupSettings.addSupervisor')}</FormLabel>
                    <InputGroup>
                        <DropdownSearch
                            loadOptions={(input) => this.loadServiceSupervisorOptions(input)}
                            onChange={async (value) => await this.setState({ selectedServiceSupervision: value })}
                            value={this.state.selectedServiceSupervision}
                        />
                        <Button
                            color="secondary"
                            disabled={!this.state.selectedServiceSupervision}
                            onClick={async () => await this.addServiceSupervisor(this.state.selectedServiceSupervision)}
                        >
                            {translateKey('common.add')}
                        </Button>
                    </InputGroup>
                </FormGroup>
            </>
        );
    }

    private async removeServiceSupervisor(selectedServiceSupervision: IServiceSupervisor) {
        await this.props.onChange(this.props.selectedServiceSupervisors.delete(selectedServiceSupervision.id));
    }

    private async addServiceSupervisor(selectedServiceSupervision: IServiceSupervisor | null) {
        if (selectedServiceSupervision && selectedServiceSupervision.id) {
            await this.props.onChange(
                this.props.selectedServiceSupervisors.set(selectedServiceSupervision.id, selectedServiceSupervision)
            );
            await this.setState({ selectedServiceSupervision: null });
        }
    }

    private async loadServiceSupervisorOptions(input: string) {
        const url = `/api/serviceSupervisors?serviceId=${this.props.serviceId}&searchText=${input}&isActive=true`;
        const result = await fetchJson(url, 'GET');

        const options = result.json.items.map((serviceSupervisor: IServiceSupervisor) => {
            return {
                disabled: !serviceSupervisor.employment.practitioner.isActivated,
                label:
                    getDisplayName(serviceSupervisor.employment.practitioner) +
                    ', ' +
                    serviceSupervisor.employment.organization.name,
                value: serviceSupervisor,
            };
        });

        return options;
    }
}
